import { PullRequestLifecycle } from "./PullRequestLifecycle";
import { PullRequestDiscussion, usePullRequestDiscussionAvailable } from "./PullRequestDiscussion";
import { PullRequestEdit } from "./PullRequestEdit";
import type { PullRequestDetailResult, PullRequestTimelineResult } from "@afar-research/contracts";
import {
  ArrowDownUpIcon,
  ExternalLinkIcon,
  GitBranchIcon,
  GitPullRequestIcon,
  MessageSquareIcon,
  MoreHorizontalIcon,
  TagIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { Component, lazy, Suspense, useState, type ReactNode } from "react";

import {
  decodePullRequestSurface,
  usePullRequestSurfaceStore,
  type PullRequestSurfaceDescriptor,
} from "../pullRequestSurfaceStore";
import { usePullRequestLiveRead } from "../hooks/usePullRequestLiveRead";
import { newCommandId } from "../lib/utils";
import { executePullRequestDiscussion } from "../pullRequestDiscussionCommand";
import { invalidatePullRequest } from "../pullRequestInvalidation";
import { usePullRequestDetail, usePullRequestTimeline } from "../state/pullRequests";
import { workSurfaceResources } from "../workSurfaceResources";
import {
  WorkspaceSurfaceHost,
  workspaceSurfaceResourceOwnerIdentity,
  type WorkspaceSurfaceDefinition,
} from "../workspaceSurfaceHost";
import { PullRequestsUnavailableState } from "./pullRequest/PullRequestsUnavailableState";
import { PullRequestLabelEditor } from "./pullRequest/PullRequestLabelEditor";
import { PullRequestReviewerPicker } from "./pullRequest/PullRequestReviewerPicker";
import { PullRequestThreadLinks } from "./pullRequest/PullRequestThreadLinks";
import { PullRequestTimelineRail } from "./pullRequest/PullRequestTimelineRail";
import { pullRequestTimelineEntries } from "./pullRequest/pullRequestTimeline.logic";
import { PullRequestMetaRow } from "./pullRequest/PullRequestMetaRow";
import { showPullRequestLinkContextMenu } from "./pullRequest/pullRequestLinkContextMenu";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "./ui/menu";
import { Button } from "./ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import { ScrollArea } from "./ui/scroll-area";
import { Toggle, ToggleGroup } from "./ui/toggle-group";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { cn } from "../lib/utils";
import { PullRequestHandoff } from "./PullRequestHandoff";
import { PreviewPanelShell } from "./preview/PreviewPanelShell";
import {
  PullRequestDetailGhost,
  PullRequestListGhost,
  PullRequestTimelineGhost,
} from "./pullRequest/PullRequestGhosts";
import {
  PullRequestActorAvatar,
  PullRequestCheckStatusIcon,
  PullRequestDiffStat,
  PullRequestStateGlyph,
  pullRequestCheckStatusLabel,
} from "./pullRequest/pullRequestPresentation";

const PullRequestCodeSurface = lazy(() => import("./PullRequestCodeSurface"));
// The description is markdown like any other; the renderer is heavy, so it arrives with the
// Summary rather than with the workspace.
const PullRequestBodyMarkdown = lazy(async () => ({
  default: (await import("./ChatMarkdown")).default as unknown as (props: {
    readonly text: string;
  }) => ReactNode,
}));

class HostedCodeBoundary extends Component<
  { children: ReactNode; url: string },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? (
      <div role="alert" className="p-4">
        <p>Hosted Code could not render.</p>
        <button onClick={() => this.setState({ failed: false })}>Retry Code renderer</button>{" "}
        <a href={this.props.url} target="_blank" rel="noreferrer">
          Open on GitHub
        </a>
      </div>
    ) : (
      this.props.children
    );
  }
}

const PULL_REQUEST_WORKSPACE_SCOPE = {
  type: "workspace",
  workspace: {
    environmentId: "pull-requests-workspace",
    projectId: "pull-requests-workspace",
  },
} as const;

function detailMatchesDescriptor(
  result: PullRequestDetailResult,
  descriptor: PullRequestSurfaceDescriptor,
): boolean {
  const identity = result.status === "ready" ? result.detail : result;
  return (
    identity.projectId === descriptor.projectId &&
    identity.repository === descriptor.repository &&
    identity.number === descriptor.number
  );
}

function PullRequestSummarySurface({
  descriptor,
  active,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly active: boolean;
}) {
  const liveRead = usePullRequestLiveRead(descriptor, { active, partition: "summary" });
  const discussionAvailable = usePullRequestDiscussionAvailable(descriptor);
  const query = usePullRequestDetail(
    liveRead.authorityEpoch === null
      ? undefined
      : {
          environmentId: descriptor.environmentId,
          input: {
            projectId: descriptor.projectId,
            repository: descriptor.repository,
            number: descriptor.number,
            authorityEpoch: liveRead.authorityEpoch,
          },
        },
  );

  if (!liveRead.connectionReady) {
    return (
      <div className="grid min-h-64 place-items-center p-6 text-center" role="status">
        <div>
          <p className="text-sm font-medium">Waiting for fresh Environment authority</p>
          <p className="mt-1 max-w-sm text-xs text-muted-foreground">
            The descriptor remains open, but Pull Request detail and viewer permissions stay
            unavailable until this Environment finishes fresh synchronization.
          </p>
        </div>
      </div>
    );
  }
  if (!liveRead.capabilitySupported) {
    return (
      <div className="grid min-h-64 place-items-center p-6 text-center" role="status">
        <div>
          <p className="text-sm font-medium">Pull Request Summary is not supported</p>
          <p className="mt-1 max-w-sm text-xs text-muted-foreground">
            This Environment does not advertise the Summary capability. Update or reconnect the
            Environment before opening Pull Request details.
          </p>
        </div>
      </div>
    );
  }
  if (liveRead.connectionGeneration === undefined) {
    return (
      <PullRequestsUnavailableState
        title="Waiting for fresh Environment authority"
        error="Pull Request detail stays unavailable until the current connection generation is known."
      />
    );
  }
  if (query.isPending && query.data === null) {
    return <PullRequestDetailGhost />;
  }
  if (query.error !== null) {
    return (
      <PullRequestsUnavailableState
        title="Pull Request Summary could not load"
        error={query.error}
        hostUrl={descriptor.url}
        onRetry={liveRead.refresh}
        role="alert"
      />
    );
  }
  if (query.data === null) return null;
  if (!detailMatchesDescriptor(query.data, descriptor)) {
    return (
      <PullRequestsUnavailableState
        title="Pull Request identity mismatch"
        error="The Environment returned detail for a different Project, repository, or number. No detail authority was accepted."
        hostUrl={descriptor.url}
        onRetry={liveRead.refresh}
        role="alert"
      />
    );
  }
  if (query.data.status === "unavailable") {
    const externalFallbackUrl =
      query.data.externalFallback._tag === "Available"
        ? query.data.externalFallback.url
        : descriptor.url;
    return (
      // The reason stays on the element the reader sees, so a failure can be told apart from a
      // read that simply has not happened.
      <div data-detail-failure={query.data.failure.reason}>
        <PullRequestsUnavailableState
          title="Pull Request Summary could not load"
          error={query.data.failure.message}
          hostUrl={externalFallbackUrl}
          onRetry={liveRead.refresh}
          role="alert"
        />
      </div>
    );
  }

  const detail = query.data.detail;
  return (
    <article
      className="h-full overflow-y-auto"
      data-authority="live"
      data-pull-request-detail={detail.number}
      data-pull-request-summary-scroll
    >
      {/* Following T3Code: what this pull request is, then what it says, then what it changes.
          The Environment and Project stay named, because the same repository can be open on
          more than one of each and a header that hides that is a header that can mislead. */}
      <header className="border-b border-border px-4 pt-3 pb-4">
        <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <PullRequestStateGlyph
            state={detail.state}
            isDraft={detail.isDraft}
            mergeability={detail.mergeability}
            baseBranch={detail.baseRefName}
          />
          <span
            className="shrink-0 font-mono tabular-nums"
            onContextMenu={(event) => {
              event.preventDefault();
              void showPullRequestLinkContextMenu({
                url: detail.url,
                position: { x: event.clientX, y: event.clientY },
              });
            }}
          >
            {detail.repository}#{detail.number}
          </span>
          <span className="truncate">{detail.projectTitle}</span>
          <PullRequestThreadLinks environmentId={descriptor.environmentId} url={detail.url} />
          <a
            aria-label="Open Pull Request on GitHub"
            className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
            href={detail.url}
            rel="noreferrer"
            target="_blank"
          >
            <ExternalLinkIcon aria-hidden="true" className="size-4" />
          </a>
        </div>
        <div className="group mt-1 flex min-w-0 items-start gap-1">
          <h2 className="min-w-0 flex-1 text-lg font-semibold leading-tight">{detail.title}</h2>
          <span className="shrink-0 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
            <PullRequestEdit active={active} descriptor={descriptor} detail={detail} />
          </span>
        </div>
        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {detail.author ? (
            <span className="inline-flex shrink-0 items-center gap-1">
              <PullRequestActorAvatar actor={detail.author} className="size-3.5" />
              <span className="max-w-32 truncate">{detail.author.login}</span>
            </span>
          ) : null}
          <span className="truncate font-mono">
            {detail.headRefName} → {detail.baseRefName}
          </span>
          <PullRequestDiffStat
            additions={detail.additions}
            deletions={detail.deletions}
            className="font-mono"
          />
          <span>{detail.changedFiles} files</span>
        </div>
        {/* Following T3Code: what can be done to this pull request sits with the pull request,
            as one action and an overflow, rather than as a panel of controls below the page. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <PullRequestLifecycle active={active} descriptor={descriptor} detail={detail} />
          {active ? (
            <Popover>
              <PopoverTrigger
                render={
                  <Button size="sm" variant="outline">
                    <GitBranchIcon aria-hidden className="size-3.5" />
                    Checkout
                    {/* The full name stays in the accessible name; the row has no space to
                        print it beside the lifecycle action it sits next to. */}
                    <span className="sr-only"> and agent handoff</span>
                  </Button>
                }
              />
              <PopoverPopup align="start" className="w-96 p-3">
                <PullRequestHandoff descriptor={descriptor} detail={detail} />
              </PopoverPopup>
            </Popover>
          ) : null}
        </div>
      </header>

      {/* Following T3Code: what the host says about this pull request reads as facts, and
          everything that acts on it sits in a section the reader can fold away. */}
      <section className="px-4 py-2.5">
        <div className="space-y-2">
          <PullRequestMetaRow icon={<UsersIcon className="size-3.5" />} label="Reviewers">
            <PullRequestReviewerPicker descriptor={descriptor} detail={detail} enabled={active} />
          </PullRequestMetaRow>
          {/* The row is shown empty only where a label could be put on it from here; on a
              provider with none to offer, an empty row is a row about nothing. */}
          {detail.labels.length > 0 || detail.capabilities.labels === true ? (
            <PullRequestMetaRow icon={<TagIcon className="size-3.5" />} label="Labels">
              <PullRequestLabelEditor descriptor={descriptor} detail={detail} enabled={active} />
            </PullRequestMetaRow>
          ) : null}
        </div>
      </section>

      <section aria-label="Description" className="px-4 pb-2">
        {detail.body.trim().length === 0 ? (
          <p className="text-xs text-muted-foreground">No description provided.</p>
        ) : (
          <Suspense fallback={<p className="whitespace-pre-wrap text-sm">{detail.body}</p>}>
            <PullRequestBodyMarkdown text={detail.body} />
          </Suspense>
        )}
      </section>

      <section aria-label="Checks" className="px-4 pb-2">
        {detail.checks.length === 0 ? (
          <p className="text-xs text-muted-foreground">No checks reported.</p>
        ) : (
          <div>
            <p className="text-xs font-medium text-muted-foreground">Checks</p>
            <div className="mt-1">
              {detail.checks.map((check, index) => (
                // The host decides how many runs share a name, so position separates them: a
                // repeated key would be a rendering fault on top of whatever the list says.
                <div
                  className="flex items-start gap-2 rounded-md px-2 py-2 text-xs leading-5 hover:bg-accent/60"
                  key={`${index}:${check.name}:${check.url ?? ""}`}
                >
                  <PullRequestCheckStatusIcon status={check.status} />
                  {check.url === null ? (
                    <span className="min-w-0 flex-1 wrap-anywhere">{check.name}</span>
                  ) : (
                    <a
                      className="min-w-0 flex-1 wrap-anywhere hover:underline"
                      href={check.url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {check.name}
                    </a>
                  )}
                  <span className="shrink-0 text-muted-foreground">
                    {pullRequestCheckStatusLabel(check.status)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {discussionAvailable ? (
        <div className="pointer-events-none sticky bottom-3 z-20 flex justify-end px-4">
          <Popover>
            <PopoverTrigger
              render={
                <Button className="pointer-events-auto shadow-md" size="sm" variant="outline">
                  <MessageSquareIcon aria-hidden className="size-3.5" />
                  Comment
                </Button>
              }
            />
            <PopoverPopup align="end" className="w-96 p-3" side="top">
              <PullRequestDiscussion active={active} descriptor={descriptor} detail={detail} />
            </PopoverPopup>
          </Popover>
        </div>
      ) : null}
    </article>
  );
}

function timelineMatchesDescriptor(
  result: PullRequestTimelineResult,
  descriptor: PullRequestSurfaceDescriptor,
): boolean {
  return (
    result.projectId === descriptor.projectId &&
    result.repository === descriptor.repository &&
    result.number === descriptor.number
  );
}

function PullRequestTimelineSurface({
  descriptor,
  active,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly active: boolean;
}) {
  const liveRead = usePullRequestLiveRead(descriptor, { active, partition: "timeline" });
  const [order, setOrder] = useState<"newest" | "oldest">("newest");
  const target =
    liveRead.authorityEpoch === null
      ? undefined
      : ({
          environmentId: descriptor.environmentId,
          input: {
            projectId: descriptor.projectId,
            repository: descriptor.repository,
            number: descriptor.number,
            authorityEpoch: liveRead.authorityEpoch,
          },
        } as const);
  const query = usePullRequestTimeline(target);

  if (!liveRead.connectionReady || liveRead.connectionGeneration === undefined) {
    return (
      <div className="grid min-h-64 place-items-center p-6 text-center" role="status">
        <div>
          <p className="text-sm font-medium">Waiting for fresh Environment authority</p>
          <p className="mt-1 max-w-sm text-xs text-muted-foreground">
            Timeline activity stays unavailable until this Environment finishes fresh
            synchronization.
          </p>
        </div>
      </div>
    );
  }
  if (!liveRead.capabilitySupported) {
    return (
      <PullRequestsUnavailableState
        title="Pull Request Timeline is not supported"
        error="This AFAR Environment does not advertise the Timeline capability."
      />
    );
  }
  if (query.isPending && query.data === null) {
    return <PullRequestTimelineGhost />;
  }
  if (query.error !== null) {
    return (
      <PullRequestsUnavailableState
        title="Pull Request Timeline could not load"
        error={query.error}
        hostUrl={descriptor.url}
        onRetry={liveRead.refresh}
        role="alert"
      />
    );
  }
  if (query.data === null) return null;
  if (!timelineMatchesDescriptor(query.data, descriptor)) {
    return (
      <PullRequestsUnavailableState
        title="Pull Request Timeline identity mismatch"
        error="The Environment returned activity for a different Project, repository, or number."
        onRetry={liveRead.refresh}
        role="alert"
      />
    );
  }
  if (query.data.status === "unavailable") {
    const externalFallbackUrl =
      query.data.externalFallback._tag === "Available"
        ? query.data.externalFallback.url
        : descriptor.url;
    return (
      <div data-timeline-failure={query.data.failure.reason}>
        <PullRequestsUnavailableState
          title="Pull Request Timeline could not load"
          error={query.data.failure.message}
          hostUrl={externalFallbackUrl}
          onRetry={liveRead.refresh}
          role="alert"
        />
      </div>
    );
  }

  const timeline = query.data;
  const timelineRevision = timeline.projectRevision;
  return (
    <article
      className="h-full overflow-y-auto"
      data-authority="live"
      data-pull-request-timeline={timeline.number}
    >
      {/* Newest first, and reversible: what happened last is what a reader opening the tab is
          asking about, but reading the change from its beginning is the other real question. */}
      <div className="flex items-center justify-end px-4 pt-3">
        <Button
          aria-label={
            order === "newest" ? "Show oldest activity first" : "Show newest activity first"
          }
          className="h-7 px-2 text-[10px] text-muted-foreground"
          onClick={() => setOrder(order === "newest" ? "oldest" : "newest")}
          size="xs"
          variant="ghost"
        >
          <ArrowDownUpIcon aria-hidden className="size-3" />
          {order === "newest" ? "Newest first" : "Oldest first"}
        </Button>
      </div>
      {/* Following T3Code: activity, commits and comments are one history on one rail, in the
          order they happened, rather than three lists each with its own clock. */}
      <PullRequestTimelineRail
        entries={pullRequestTimelineEntries(timeline)}
        order={order}
        reacting={{
          descriptor,
          projectRevision: timeline.projectRevision,
          resourceVersion: timeline.resourceVersion,
          // The host is the authority on who may react; this only decides whether to offer it.
          canReact: active && liveRead.connectionReady,
        }}
        editing={
          // The Environment's own gate decides; the rail only offers what it will take.
          active && liveRead.connectionReady && timelineRevision !== undefined
            ? {
                onSave: (comment, body) =>
                  void executePullRequestDiscussion({
                    environmentId: descriptor.environmentId,
                    projectId: descriptor.projectId,
                    repository: descriptor.repository,
                    number: descriptor.number,
                    expectedRevision: timelineRevision,
                    resourceVersion: timeline.resourceVersion,
                    commandId: newCommandId(),
                    action: {
                      kind: "edit-comment",
                      commentId: comment.id,
                      commentVersion: comment.updatedAt,
                      body,
                    },
                  }).then(() =>
                    invalidatePullRequest({
                      environmentId: descriptor.environmentId,
                      projectId: descriptor.projectId,
                      repository: descriptor.repository,
                      number: descriptor.number,
                      partitions: ["summary", "timeline"],
                    }),
                  ),
              }
            : null
        }
        reviewThreads={timeline.reviewThreads}
      />
    </article>
  );
}

export function PullRequestDetailSurface({
  descriptor,
}: {
  readonly descriptor: PullRequestSurfaceDescriptor;
}) {
  const [activeView, setActiveView] = useState<"summary" | "timeline" | "code">("summary");
  const [codeVisited, setCodeVisited] = useState(false);
  const [timelineVisited, setTimelineVisited] = useState(false);
  const showTimeline = () => {
    setTimelineVisited(true);
    setActiveView("timeline");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The same segmented control the thread surfaces wear, following T3Code, rather than a
          row of bare buttons that reads as three unrelated links. */}
      <nav
        aria-label={`Pull Request #${descriptor.number} views`}
        className="flex min-w-0 items-center gap-1 overflow-x-auto border-b border-border px-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <ToggleGroup
          onValueChange={(next) => {
            const view = next[0];
            if (view === "timeline") showTimeline();
            else if (view === "code") {
              setCodeVisited(true);
              setActiveView("code");
            } else if (view === "summary") setActiveView("summary");
          }}
          size="segmented"
          value={[activeView]}
          variant="segmented"
        >
          <Toggle aria-label={`Show Pull Request #${descriptor.number} Summary`} value="summary">
            Summary
          </Toggle>
          <Toggle aria-label={`Show Pull Request #${descriptor.number} Timeline`} value="timeline">
            Timeline
          </Toggle>
          <Toggle
            aria-label={`Show Pull Request #${descriptor.number} Code`}
            onFocus={() => setCodeVisited(true)}
            onPointerEnter={() => setCodeVisited(true)}
            value="code"
          >
            Code
          </Toggle>
        </ToggleGroup>
      </nav>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {codeVisited ? (
          <div className={cn("absolute inset-0", activeView !== "code" && "invisible")}>
            <HostedCodeBoundary url={descriptor.url}>
              <Suspense fallback={<PullRequestListGhost rows={5} />}>
                <PullRequestCodeSurface descriptor={descriptor} />
              </Suspense>
            </HostedCodeBoundary>
          </div>
        ) : null}
        <div className={cn("absolute inset-0", activeView !== "summary" && "invisible")}>
          <PullRequestSummarySurface active={activeView === "summary"} descriptor={descriptor} />
        </div>
        {timelineVisited ? (
          <div className={cn("absolute inset-0", activeView !== "timeline" && "invisible")}>
            <PullRequestTimelineSurface
              active={activeView === "timeline"}
              descriptor={descriptor}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const PULL_REQUEST_SURFACE_DEFINITIONS = [
  {
    kind: "pull-request",
    scope: "workspace",
    identityVersion: 1,
    persistence: "session",
    lifetime: "active",
    decode: decodePullRequestSurface,
    acquire: ({ descriptor, scope }) =>
      workSurfaceResources.acquire({
        owner: {
          kind: "workspace-surface",
          identity: workspaceSurfaceResourceOwnerIdentity(scope, descriptor),
        },
        resource: { kind: "surface-lease", identity: descriptor.id },
        lifetime: "open-owner",
      }),
    render: ({ descriptor, renderRegion }) =>
      renderRegion(<PullRequestDetailSurface key={descriptor.id} descriptor={descriptor} />),
  },
] satisfies readonly WorkspaceSurfaceDefinition<PullRequestSurfaceDescriptor>[];

export function PullRequestSurfacePanel({
  onSelectionCleared,
}: {
  /** Told when the panel stops showing a pull request, so the address can stop naming one. */
  readonly onSelectionCleared?: () => void;
} = {}) {
  const panel = usePullRequestSurfaceStore((state) => state.panel);
  const activate = usePullRequestSurfaceStore((state) => state.activate);
  const closeSurface = usePullRequestSurfaceStore((state) => state.close);
  const closeOthers = usePullRequestSurfaceStore((state) => state.closeOthers);
  const closeToRight = usePullRequestSurfaceStore((state) => state.closeToRight);
  const closeAllSurfaces = usePullRequestSurfaceStore((state) => state.closeAll);
  const close = (surfaceId: string) => {
    closeSurface(surfaceId);
    // The store decides what is selected next; the address only stops naming one when nothing
    // is left to name.
    if (usePullRequestSurfaceStore.getState().panel.activeSurfaceId === null)
      onSelectionCleared?.();
  };
  /** Every bulk close ends the same way: if nothing is left, nothing is selected. */
  const closeMany = (closeThem: () => void) => {
    closeThem();
    if (usePullRequestSurfaceStore.getState().panel.activeSurfaceId === null)
      onSelectionCleared?.();
  };
  // Where the panel starts before anyone drags it: roughly half the viewport, because a Pull
  // Request needs more room than a chat preview. The shell clamps it against the row so the list
  // beside it keeps its own minimum width, and remembers it per device under this page's own key.
  const defaultWidth =
    typeof window === "undefined" ? 640 : Math.max(360, Math.floor(window.innerWidth / 2));

  return (
    <WorkspaceSurfaceHost
      definitions={PULL_REQUEST_SURFACE_DEFINITIONS}
      renderRegion={(_presentation, content) => (
        // No `open`: the surface host unmounts this region when the panel is put away, which is
        // what releases the surface's lease, so there is no collapsed state for the shell to
        // animate through.
        <PreviewPanelShell
          defaultWidth={defaultWidth}
          mode="inline"
          widthStorageKey="afar-research:pull-request-panel-width"
        >
          {/* The same strip the thread's right panel wears, so a Pull Request tab here and a
              preview tab there read as one control rather than two inventions. */}
          <div className="workspace-topbar gap-1 pl-2 pr-3" data-right-panel-tabbar>
            <ScrollArea
              className="min-w-0 flex-1 rounded-none"
              data-right-panel-tab-list
              hideScrollbars
              scrollFade
            >
              <div className="flex h-full w-max min-w-full items-center gap-1">
                {panel.surfaces.map((surface, index) => {
                  const active = surface.id === panel.activeSurfaceId;
                  return (
                    <div
                      className={cn(
                        "group flex h-7 min-w-25 max-w-44 shrink-0 items-center gap-1.5 rounded-md px-2 text-sm",
                        active
                          ? "bg-accent text-foreground"
                          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                      )}
                      data-active-tab={active}
                      key={surface.id}
                    >
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <button
                              aria-label={`Activate ${surface.repository} #${surface.number}`}
                              className="flex min-w-0 flex-1 items-center gap-1.5"
                              onClick={() => activate(surface.id)}
                              type="button"
                            />
                          }
                        >
                          <GitPullRequestIcon aria-hidden="true" className="size-3.5 shrink-0" />
                          <span className="truncate">#{surface.number}</span>
                        </TooltipTrigger>
                        <TooltipPopup>
                          {surface.repository} #{surface.number}
                        </TooltipPopup>
                      </Tooltip>
                      {/* Closing more than one at a time lives behind this menu rather than on
                          the ×, which stays the one-press way to close the tab under the
                          pointer. It is a menu and not the desktop context menu so the Host Web
                          Client keeps the same way out. */}
                      <Menu>
                        <MenuTrigger
                          render={
                            <button
                              aria-label={`Actions for Pull Request #${surface.number}`}
                              className="flex size-4 shrink-0 items-center justify-center rounded opacity-0 hover:bg-muted focus-visible:opacity-100 group-hover:opacity-100 data-[popup-open]:opacity-100"
                              type="button"
                            />
                          }
                        >
                          <MoreHorizontalIcon aria-hidden="true" className="size-3" />
                        </MenuTrigger>
                        <MenuPopup align="start" side="bottom">
                          <MenuItem onClick={() => close(surface.id)}>Close</MenuItem>
                          <MenuItem
                            disabled={panel.surfaces.length < 2}
                            onClick={() => closeMany(() => closeOthers(surface.id))}
                          >
                            Close others
                          </MenuItem>
                          <MenuItem
                            disabled={index === panel.surfaces.length - 1}
                            onClick={() => closeMany(() => closeToRight(surface.id))}
                          >
                            Close to the right
                          </MenuItem>
                          <MenuItem onClick={() => closeMany(closeAllSurfaces)}>Close all</MenuItem>
                        </MenuPopup>
                      </Menu>
                      <button
                        aria-label={`Close Pull Request #${surface.number}`}
                        className="flex size-4 shrink-0 items-center justify-center rounded opacity-0 hover:bg-muted focus-visible:opacity-100 group-hover:opacity-100"
                        onClick={() => close(surface.id)}
                        type="button"
                      >
                        <XIcon aria-hidden="true" className="size-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{content}</div>
        </PreviewPanelShell>
      )}
      scope={PULL_REQUEST_WORKSPACE_SCOPE}
      state={panel}
      useSheet={false}
    />
  );
}
